/**
 * Skeleton Loading Components for Tenant Landing V2
 * Optimized for mobile-first design with smooth animations
 */

import { cn } from "@/lib/utils";

interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className }: SkeletonProps) {
  return (
    <div className={cn("animate-pulse rounded-md bg-muted/50", className)} />
  );
}

export function HeroSkeleton() {
  return (
    <section className="relative w-full">
      {/* Banner skeleton */}
      <div className="relative h-[40vh] md:h-[50vh] lg:h-[60vh] bg-gradient-to-br from-muted/50 via-muted/30 to-background animate-pulse">
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
      </div>

      {/* Content skeleton */}
      <div className="container mx-auto px-4 -mt-16 md:-mt-24 relative z-10">
        <div className="flex flex-col items-center text-center space-y-4">
          {/* Logo skeleton */}
          <Skeleton className="h-20 w-20 md:h-24 md:w-24 rounded-full" />

          {/* Title skeleton */}
          <Skeleton className="h-8 md:h-10 w-64 md:w-80" />

          {/* Description skeleton */}
          <Skeleton className="h-4 w-full max-w-2xl" />
          <Skeleton className="h-4 w-3/4 max-w-xl" />

          {/* Buttons skeleton */}
          <div className="flex gap-3 pt-4">
            <Skeleton className="h-11 w-40" />
            <Skeleton className="h-11 w-40" />
          </div>

          {/* Status badge skeleton */}
          <Skeleton className="h-6 w-32 rounded-full mt-2" />
        </div>
      </div>
    </section>
  );
}

export function ServicesSkeletonGrid() {
  return (
    <section className="px-4 py-12 max-w-screen-xl mx-auto">
      {/* Header skeleton */}
      <div className="text-center mb-10 space-y-3">
        <div className="flex items-center justify-center gap-2 mb-3">
          <Skeleton className="h-5 w-5" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-4 w-96 mx-auto" />
      </div>

      {/* Grid skeleton */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <ServiceCardSkeleton key={i} />
        ))}
      </div>
    </section>
  );
}

export function ServiceCardSkeleton() {
  return (
    <div className="border-2 rounded-lg overflow-hidden">
      {/* Image skeleton */}
      <Skeleton className="aspect-video w-full" />

      {/* Content skeleton */}
      <div className="p-4 space-y-3">
        {/* Title and price */}
        <div className="flex items-start justify-between gap-2">
          <Skeleton className="h-6 flex-1" />
          <Skeleton className="h-6 w-20" />
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>

        {/* Duration */}
        <div className="pt-2 border-t">
          <Skeleton className="h-4 w-24" />
        </div>
      </div>
    </div>
  );
}

export function ReviewsSkeleton() {
  return (
    <section className="py-12 bg-zinc-50 dark:bg-zinc-950">
      <div className="container mx-auto px-4">
        {/* Header skeleton */}
        <div className="text-center mb-8 space-y-4">
          <Skeleton className="h-8 w-12 mx-auto rounded-full" />
          <Skeleton className="h-10 w-96 mx-auto" />
          <Skeleton className="h-4 w-full max-w-2xl mx-auto" />
        </div>

        {/* Stats card skeleton */}
        <div className="max-w-4xl mx-auto mb-8 border rounded-lg p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Average rating */}
            <div className="text-center space-y-3">
              <Skeleton className="h-16 w-24 mx-auto" />
              <Skeleton className="h-6 w-32 mx-auto" />
              <Skeleton className="h-4 w-48 mx-auto" />
            </div>

            {/* Distribution */}
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-4 w-12" />
                  <Skeleton className="h-3 flex-1" />
                  <Skeleton className="h-4 w-12" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Reviews grid skeleton */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <ReviewCardSkeleton key={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function ReviewCardSkeleton() {
  return (
    <div className="border rounded-lg p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Skeleton className="h-10 w-10 rounded-full" />
          <Skeleton className="h-5 w-24" />
        </div>
        <Skeleton className="h-5 w-20" />
      </div>

      {/* Comment */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-4/6" />
      </div>

      {/* Services */}
      <div className="flex gap-2 flex-wrap">
        <Skeleton className="h-6 w-20 rounded-full" />
        <Skeleton className="h-6 w-24 rounded-full" />
      </div>

      {/* Date */}
      <Skeleton className="h-3 w-32" />
    </div>
  );
}

export function AboutSkeleton() {
  return (
    <section className="px-4 py-12 max-w-screen-xl mx-auto">
      <div className="grid md:grid-cols-2 gap-8">
        {/* About text */}
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>

        {/* Working hours */}
        <div className="space-y-4">
          <Skeleton className="h-8 w-56" />
          <div className="space-y-3">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="flex justify-between">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-5 w-40" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function CTASkeleton() {
  return (
    <section className="py-12 bg-primary/5">
      <div className="container mx-auto px-4 text-center space-y-6">
        <Skeleton className="h-10 w-96 mx-auto" />
        <Skeleton className="h-5 w-full max-w-2xl mx-auto" />
        <Skeleton className="h-12 w-56 mx-auto rounded-full" />
      </div>
    </section>
  );
}

export function FullPageSkeleton() {
  return (
    <div className="min-h-dvh flex flex-col bg-background">
      <main className="flex-1">
        <HeroSkeleton />
        <AboutSkeleton />
        <ReviewsSkeleton />
        <ServicesSkeletonGrid />
        <CTASkeleton />
      </main>

      {/* Footer skeleton */}
      <footer className="border-t bg-muted/30">
        <div className="container mx-auto px-4 py-8 space-y-4">
          <Skeleton className="h-6 w-48 mx-auto" />
          <div className="flex justify-center gap-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-4 w-24" />
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
